<template>
  <div class="shop-view">
    <ftu-block
      dismiss-key="shop"
      icon="local_offer"
      title="This is the Shop"
      description="The Shop is where you can search cards for sale across multiple platforms. Use the powerful filters and sorts to find your next big purchase and save your search so you can check it quickly each day."
    />

    <page-header title="Shop">
      <template #actions>
        <align small-gap>
          <saved-filters
            v-show="!isFeedView"
            type="shop"
            :query="query"
            :filters="filters"
            :is-filtering="isFiltering"
            @select="applySavedFilter"
          />

          <icon-button
            icon="dynamic_feed"
            tooltip="View Full Feed"
            :class="{ success: isFeedView }"
            @click="isFeedView = true"
          />

          <icon-button
            v-show="isFeedView"
            icon="close"
            tooltip="Close Feed"
            @click="isFeedView = false"
          />
        </align>
      </template>
    </page-header>

    <align
      v-show="!isFeedView"
      flex
      collapse-end
      direction="flex-start"
    >
      <shared-filters
        show-recent-searches
        :sales-platforms="platformOptions"
        :filters.sync="filters"
        :query.sync="query"
        :properties="defaultFilters"
        @clear="clearFilters"
      />

      <align>
        <align small-gap>
          <icon-button
            icon="list"
            :class="{ success: shopViewType === 'list' }"
            @click="shopViewType = 'list'"
          />

          <icon-button
            icon="view_module"
            :class="{ success: shopViewType === 'grid' }"
            @click="shopViewType = 'grid'"
          />
        </align>

        <direction-sort-options
          :sort.sync="sort"
          :direction.sync="direction"
          :options="sortOptions"
        />
      </align>
    </align>

    <filter-results
      v-show="!isFeedView"
      :is-filtering="isFiltering"
      :filters="filters"
      :results="numResults && numResults < 10000 ? numResults : null"
      @clear="clearFilters"
    />

    <div class="results">
      <p class="affiliate-text">
        If you purchase an item from the results below after clicking the link, Card Ladder may be compensated at no additional cost to you.
      </p>

      <p
        v-if="isFeedView && feedCapNotice"
        class="feed-cap-notice"
      >
        {{ feedCapNotice }}
      </p>

      <empty-state
        v-if="showEmptyState"
        icon="search_off"
        :title="isFeedView ? 'Your feed is empty' : 'There are no results for your query'"
        :description="isFeedView ? 'Make sure you have at least one saved search' : 'Try searching for something else'"
      />

      <div v-if="shopViewType === 'list'" class="list">
        <shop-list-item
          v-for="item in listings"
          :key="item.id"
          :item="item"
          as="a"
          :href="item.url"
          target="_blank"
          rel="noopener noreferrer"
        />
      </div>

      <div v-else class="grid">
        <shop-listing-card
          v-for="item in listings"
          :key="item.id"
          :item="item"
          :href="item.url"
          target="_blank"
          rel="noopener noreferrer"
        />
      </div>

      <infinite-scroll
        :loading="loading"
        :has-more-data="hasMoreData"
        @load="loadMore"
      />

      <scroll-top />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import debounce from 'lodash.debounce';
import clone from 'lodash.clonedeep';
import isEqual from 'lodash.isequal';
import * as Sentry from '@sentry/vue';

import {
  LISTING_SORT_OPTIONS,
  DEFAULT_SHOP_FILTERS,
  SELLING_PLATFORM_OPTIONS,
} from '@cardladder/shared/constants';
import { getShopFeedCapNotice, loadShopListingsFeed } from '@cardladder/shared/utils/shopFeed.util';

import DirectionSortOptions from '@cardladder/shared/components/DirectionSortOptions';
import FilterResults from '@/components/FilterResults';
import SharedFilters from '@/components/SharedFilters';
import ShopListingCard from '@cardladder/shared/components/ShopListingCard';
import ShopListItem from '@cardladder/shared/components/ShopListItem';
import { searchService, authService } from '@/services';

export default {
  components: {
    DirectionSortOptions,
    FilterResults,
    SharedFilters,
    ShopListingCard,
    ShopListItem,
  },

  data() {
    return {
      limit: 20,
      listings: [],
      numResults: 0,
      loading: false,
      loaded: false,
      hasMoreData: true,
      page: 1,
      sortOptions: LISTING_SORT_OPTIONS,
      defaultFilters: DEFAULT_SHOP_FILTERS,
      platformOptions: SELLING_PLATFORM_OPTIONS.filter(({ shop }) => shop === true),
      feedCapNotice: null,
    };
  },

  created() {
    this.reset = debounce(this.reset, 500);
    this.reset();
  },

  beforeDestroy() {
    this.$store.commit('SET_LISTINGS_FEED_VIEW', false);
  },

  watch: {
    sort() {
      this.reset();
    },

    direction() {
      this.reset();
    },

    filters: {
      handler() {
        this.reset();
      },
      deep: true,
    },

    query() {
      this.reset();
    },

    page(value) {
      if (value > 1) {
        this.search();
      }
    },

    isFeedView() {
      this.reset();
    },

    '$route.query.query': {
      handler(value) {
        if (value) {
          this.query = value;
          this.$router.replace({ query: {} });
        }
      },
      immediate: true,
    },
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
      listingsFeed: (state) => state.listingsFeed,
    }),

    shopViewType: {
      get() {
        return this.$store.state.shopViewType;
      },

      set(value) {
        this.$store.commit('SET_SHOP_VIEW_TYPE', value);
      },
    },

    sort: {
      get() {
        const activeSort = this.sortOptions.find((value) => value === this.$store.state.listingsSort);

        if (activeSort) {
          return this.$store.state.listingsSort;
        }

        return this.sortOptions[0];
      },

      set(value) {
        this.$store.commit('SET_LISTINGS_SORT', value);
      },
    },

    direction: {
      get() {
        return this.$store.state.listingsDirection;
      },

      set(value) {
        this.$store.commit('SET_LISTINGS_DIRECTION', value);
      },
    },

    filters: {
      get() {
        return this.$store.state.listingsFilterMap;
      },

      set(value) {
        this.$store.commit('SET_LISTINGS_FILTERS', value);
      },
    },

    query: {
      get() {
        return this.$store.state.listingsQuery;
      },

      set(value) {
        this.$store.commit('SET_LISTINGS_QUERY', value);
      },
    },

    isFeedView: {
      get() {
        return this.$store.state.listingsFeedView;
      },

      set(value) {
        this.$store.commit('SET_LISTINGS_FEED_VIEW', value);
      },
    },

    showEmptyState() {
      return !this.loading && this.loaded && this.listings.length === 0;
    },

    isFiltering() {
      return Boolean(this.query && typeof this.query === 'string' && this.query?.trim().length > 0) || !isEqual(this.filters, this.defaultFilters);
    },
  },

  methods: {
    reset() {
      this.page = 1;
      this.hasMoreData = true;
      this.loaded = false;
      this.feedCapNotice = null;

      if (this.isFeedView) {
        this.loadFeed();
      } else {
        this.search(true);
      }
    },

    loadMore() {
      this.page += 1;
    },

    async search(reset = false) {
      try {
        this.loading = true;

        const data = await searchService.search({
          index: 'listings',
          query: this.query,
          page: this.page,
          limit: this.limit,
          sort: this.sort,
          direction: this.direction,
          filters: {
            ...this.filters,
            endDate: 'now',
          },
        });

        const listings = (data.hits || []).map((hit) => ({
          ...hit,
          price: hit.price === null ? 0 : hit.price,
        }));

        this.numResults = data.totalHits;

        if (reset) {
          this.listings = listings;
        } else {
          this.listings = [...this.listings, ...listings];
        }

        if (listings.length < this.limit) {
          this.hasMoreData = false;
        }
      } catch (e) {
        Sentry.captureException(e);
      }

      this.loaded = true;
      this.loading = false;
    },

    async loadFeed() {
      this.listings = [];
      this.loading = true;

      if (this.listingsFeed.length) {
        this.listings = this.listingsFeed;
      } else {
        try {
          const savedFilters = await authService.getSavedFilters(this.user.uid, 'shop');
          const {
            listings,
            totalSavedFilters,
            searchedSavedFilters,
            savedSearchLimit,
          } = await loadShopListingsFeed({
            savedFilters,
            searchService,
            defaultFilters: this.defaultFilters,
          });

          this.listings = listings;
          this.feedCapNotice = getShopFeedCapNotice({
            totalSavedFilters,
            searchedSavedFilters,
            savedSearchLimit,
          });
          this.$store.commit('SET_LISTINGS_FEED', listings);
        } catch (e) {
          Sentry.captureException(e);
        }
      }

      this.loaded = true;
      this.loading = false;
      this.hasMoreData = false;
    },

    clearFilters() {
      this.filters = clone(this.defaultFilters);
    },

    applySavedFilter(savedFilter) {
      this.query = savedFilter.query;
      this.filters = {
        ...clone(this.defaultFilters),
        ...savedFilter.filters,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.shop-view {
  @include container;
  min-height: 100%;
  display: flex;
  flex-direction: column;
}

.results {
  position: relative;
  flex: 1;
  margin-top: $gp;
}

.feed-cap-notice {
  margin: 0 0 $gp * .5;
  color: $color-gray-3;
  font-size: .875rem;
}

.list {
  display: flex;
  flex-direction: column;

  @media($medium-down) {
    margin: 0 (-$gp);
  }
}

.grid {
  @include grid-panel;
}
</style>
